import React from "react";
import { useTranslation } from "react-i18next";
import { HelpHint } from "./processSidebarUi";
import type { ProcessGraphState } from "./useProcessGraph";

/** What the graph draws. Drawing fewer paths never changes the population described in Cases. */
export default function ProcessGraphCaseControl({ state }: { state: ProcessGraphState }) {
  const { t } = useTranslation();
  if (state.comparePair) return null;
  if (state.highlightedInstance) {
    const current =
      state.selection?.kind === "node" ? state.selection.occurrence?.stepIndex : undefined;
    return (
      <div
        className="max-w-full rounded-md border border-outline bg-surface px-3 py-2 text-xs shadow-sm"
        aria-live="polite"
      >
        <p className="font-medium text-text">{t("processPanel.casePath")}</p>
        {current !== undefined && (
          <p className="mt-0.5 text-text-dim">
            {t("processPanel.caseStepPosition", {
              current: current + 1,
              total: state.highlightedInstance.steps.length,
            })}
          </p>
        )}
      </div>
    );
  }
  if (state.highlightedInstanceId) return null;
  const { appliedScope, applyScope } = state;
  const drawn = state.selectedInstanceIds.length;
  const total = state.filteredInstances.length;
  // Revealed paths draw every case, whatever was hand-picked underneath.
  const chosen = appliedScope.excludedIds.length > 0 && !state.allPathsRevealed;
  // Clearing hand-picked cases hands the choice back to the size of the map.
  const setMode = (commonPaths: boolean | undefined) =>
    applyScope({ ...appliedScope, commonPaths, excludedIds: [] });
  // What is drawn, how many cases that covers, and the one move away from it.
  const view: {
    label: string;
    detail?: string;
    help?: string;
    action?: { label: string; commonPaths: boolean | undefined };
  } = chosen
    ? {
        label: t("processPanel.graphChosenCases", { count: drawn }),
        action: { label: t("processPanel.clearGraphCases"), commonPaths: undefined },
      }
    : total === 1 && drawn === 1
      ? { label: t("processPanel.graphSingleCaseSummary") }
      : state.simplified
        ? {
            label: t("processPanel.graphSimplified"),
            detail: state.commonPathsExample
              ? t("processPanel.graphSimplifiedExample", { total })
              : t("processPanel.graphSimplifiedCases", { count: drawn, total }),
            help: state.commonPathsExample
              ? t("processPage.commonPathsExampleHelp")
              : t("processPanel.graphSimplifyHelp"),
            action: { label: t("processPanel.showAllPaths"), commonPaths: false },
          }
        : {
            label: t("processPanel.graphAllPaths"),
            detail: t("processPanel.graphAllPathsCases", { count: total }),
            help: t("processPanel.graphSimplifyHelp"),
            action: state.canSimplify
              ? { label: t("processPanel.simplifyGraph"), commonPaths: true }
              : undefined,
          };
  const { action } = view;
  // Sized and framed like the zoom group beside it, with the action as its own segment.
  return (
    <div
      className={`pointer-events-auto inline-flex h-8 max-w-full items-stretch overflow-hidden rounded-md border text-xs shadow-lift ${
        chosen
          ? "border-info/30 bg-info/10 text-info-strong"
          : "border-outline-strong bg-surface text-text"
      }`}
    >
      <span className="flex min-w-0 items-center gap-1.5 pl-2.5 pr-2">
        <span className="min-w-0 truncate" aria-live="polite">
          <span className="font-medium">{view.label}</span>
          {view.detail && <span className="ml-1.5 text-text-dim">{view.detail}</span>}
        </span>
        {view.help && (
          <HelpHint
            standalone
            label={t("processPanel.graphHelpLabel")}
            text={view.help}
            side="bottom"
            align="start"
          />
        )}
      </span>
      {action && (
        <button
          type="button"
          onClick={() => setMode(action.commonPaths)}
          className={`shrink-0 border-l px-2.5 font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info/30 ${
            chosen
              ? "border-info/30 hover:bg-info/15"
              : "border-outline text-text-mid hover:bg-surface-strong hover:text-text"
          }`}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
